import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';

export default function Hero() {
  return (
    <header className="relative min-h-screen flex items-center justify-center bg-navy-900 overflow-hidden">
      {/* Ambient background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-burgundy/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gold/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-navy-700/50 rounded-full blur-[150px]" />
      </div>

      {/* Decorative floating crowns */}
      <div className="absolute top-32 left-[10%] text-gold/10 text-8xl animate-float hidden lg:block">
        <FontAwesomeIcon icon={faCrown} />
      </div>
      <div className="absolute bottom-32 right-[10%] text-gold/10 text-6xl animate-float-delayed hidden lg:block">
        <FontAwesomeIcon icon={faCrown} />
      </div>

      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A227' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Main content container with invitation frame */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 py-20">
        {/* Elegant invitation frame */}
        <div className="relative border border-gold/20 p-8 md:p-16 lg:p-20">
          {/* Corner decorations */}
          <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-gold/50" />
          <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-gold/50" />
          <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-gold/50" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-gold/50" />

          {/* Inner frame */}
          <div className="absolute inset-4 md:inset-8 border border-gold/10 pointer-events-none" />

          {/* Content */}
          <div className="relative text-center stagger-children">
            {/* Co-organizers badge */}
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 mb-10 px-8 py-4 border border-gold/30 bg-navy-800/50">
              <span className="text-gold font-serif tracking-[0.2em] text-xs uppercase">
                Organisé par
              </span>
              <div className="flex items-center gap-6">
                <img
                  src="/logo_ufe.png"
                  alt="UFE Roumanie"
                  className="h-10 w-auto brightness-0 invert opacity-90"
                />
                <span className="text-gold/30 font-light text-2xl">&</span>
                <img
                  src="/pullman-logo.png"
                  alt="Pullman Bucharest"
                  className="h-24 w-auto brightness-0 invert opacity-90"
                />
              </div>
            </div>

            {/* Crown icon */}
            <div className="crown-icon text-5xl mb-8">
              <FontAwesomeIcon icon={faCrown} />
            </div>

            {/* Main headline */}
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl md:text-display-lg lg:text-display-xl text-cream mb-6 leading-[0.95]">
              <span className="block">Qui sera le</span>
              <span className="text-gradient-gold italic font-medium">Roi</span>
              <span className="text-cream/60 mx-3 font-light">ou la</span>
              <span className="text-gradient-gold italic font-medium">Reine</span>
              <span className="text-cream">?</span>
            </h1>

            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-4 my-10">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold/50" />
              <div className="w-2 h-2 rotate-45 border border-gold/50" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold/50" />
            </div>

            {/* Subtitle */}
            <p className="font-body text-lg md:text-xl text-cream/70 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
              Rejoignez la communauté UFE pour l&apos;évènement : Galette des Rois.
              <span className="block mt-2 text-cream/50">Une tradition gourmande à partager en famille au coeur de Bucarest.</span>
            </p>

            {/* Event details */}
            <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-10 text-cream/80 font-body">
              <div className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <svg className="w-4 h-4 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                <div className="text-left">
                  <span className="block text-gold/60 text-xs uppercase tracking-wider">Date</span>
                  <span className="font-serif text-lg">Samedi 10 Janvier 2026</span>
                </div>
              </div>

              <div className="hidden md:block w-px h-12 bg-gold/20" />

              <div className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <svg className="w-4 h-4 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div className="text-left">
                  <span className="block text-gold/60 text-xs uppercase tracking-wider">Heure</span>
                  <span className="font-serif text-lg">Dès 15h00</span>
                </div>
              </div>

              <div className="hidden md:block w-px h-12 bg-gold/20" />

              <a
                href="https://lagalette.ro/en/homepage/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <svg className="w-4 h-4 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </span>
                <div className="text-left">
                  <span className="block text-gold/60 text-xs uppercase tracking-wider">Lieu</span>
                  <span className="font-serif text-lg group-hover:text-gold transition-colors">La Galette, Pullman</span>
                </div>
                <img
                  src="/la-galette-logo.png"
                  alt="La Galette"
                  className="h-10 w-auto ml-3 brightness-0 invert opacity-80 group-hover:opacity-100 transition-opacity"
                />
              </a>
            </div>

            {/* CTA Button */}
            <div className="mt-14">
              <a
                href="#rsvp"
                className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-navy-900 px-10 py-5 font-serif font-semibold text-lg tracking-wide transition-all duration-300 hover:shadow-gold"
              >
                <span>Réserver ma place</span>
                <svg
                  className="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/40 animate-bounce">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </header>
  );
}
